import { initialize } from 'launchdarkly-js-client-sdk';
import { launchDarklyLogger } from './LaunchDarklyLogger';
import { FeatureFlagOverrides } from './FeatureFlagOverrides';
/**
 * LaunchDarkly service for feature flag management.
 * Uses a singleton to reduce network calls.
 *
 * This implementation avoids using the React LaunchDarkly SDK because the application
 * includes Web Components that wrap React components. Initializing multiple LDClient
 * instances (e.g., per component or via React context) could lead to redundant network
 * requests and inconsistent flag evaluations. Instead, this service provides a shared,
 * centralized client for consistent and efficient flag access across the entire app.
 */
export class LaunchDarklyService {
    constructor() {
        this.context = {
            kind: 'user',
            key: 'anonymous'
        };
        this.options = {
            bootstrap: 'localStorage',
            streaming: true,
            logger: launchDarklyLogger
        };
        this.initializationTimeoutSeconds = 4;
        if (typeof window === 'undefined') {
            return;
        }
        const envKey = window.LD_CID;
        if (!envKey) {
            console.error('LaunchDarkly environment key is missing.');
            return;
        }
        try {
            this.ldClient = initialize(envKey, this.context, this.options);
        }
        catch {
            console.error('Failed to initialize LaunchDarkly client.');
        }
    }
    identify(context) {
        if (!this.ldClient || typeof this.ldClient.identify !== 'function') {
            return;
        }
        this.ldClient.identify(context);
    }
    static getInstance() {
        if (!this.instance) {
            this.instance = new LaunchDarklyService();
        }
        return this.instance;
    }
    getClient() {
        return this.ldClient;
    }
    /**
     * On timeout the SDK rejects; that is caught and swallowed so a slow or
     * unreachable LaunchDarkly endpoint cannot hang the caller — flag reads then
     * fall back to defaults.
     */
    async waitForInitialization() {
        if (this.ldClient &&
            typeof this.ldClient.waitForInitialization === 'function') {
            try {
                await this.ldClient.waitForInitialization(this.initializationTimeoutSeconds);
            }
            catch {
                console.error('Error during LaunchDarkly initialization.');
            }
        }
    }
    getFlag(flagKey, defaultValue) {
        const override = FeatureFlagOverrides.get(flagKey);
        if (override !== undefined) {
            return override;
        }
        if (!this.ldClient) {
            console.warn('LaunchDarkly client is not initialized. Returning default value for flag.');
            return defaultValue;
        }
        try {
            this.identify(this.context);
            return this.ldClient.variation(flagKey, defaultValue);
        }
        catch {
            console.error('Error fetching flag value. Returning default value.');
            return defaultValue;
        }
    }
}
